'use client';

import React from 'react';

import Image from 'next/image';

import { Minus, Plus } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { useAddOnDisplayName } from '@/hooks/use-add-on-display-name';
import { usePublicPricing } from '@/hooks/use-public-pricing';
import { cn, formatPrice } from '@/lib/utils';

import type { BookingAddOnDefinition } from './addOnsCatalog';

export interface AddOnCardLabels {
  /** Visible add-on title. */
  name: string;
  /** Aria label for the decrease button, e.g. "Decrease Balcony cleaning quantity". */
  decreaseQuantity: string;
  /** Aria label for the increase button, e.g. "Increase Balcony cleaning quantity". */
  increaseQuantity: string;
}

interface AddOnCardViewProps {
  addOn: BookingAddOnDefinition;
  quantity: number;
  onToggle: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  onChange?: (addOnId: string, quantity: number) => void;
  className?: string;
  disabled?: boolean;
  squareMeters?: number; // For per-square-meter pricing
  max?: number; // Upper bound for the quantity counter
  /** Unit price to display. Defaults to the catalogue price. */
  price?: number;
  labels: AddOnCardLabels;
}

/**
 * Presentational add-on card (icon + title + price + quantity counter).
 * Copy is passed in so the card can render outside the booking flow, where the
 * `booking` message namespace is not loaded.
 */
export const AddOnCardView: React.FC<AddOnCardViewProps> = ({
  addOn,
  quantity,
  onToggle,
  onIncrement,
  onDecrement,
  onChange,
  className,
  disabled = false,
  squareMeters,
  max,
  price,
  labels,
}) => {
  const {
    name: addOnName,
    decreaseQuantity: decreaseLabel,
    increaseQuantity: increaseLabel,
  } = labels;
  const isSelected = quantity >= 1;
  const isAtMax = typeof max === 'number' && quantity >= max;
  const unitPrice = price ?? addOn.price;

  // Calculate price: per square meter if applicable, otherwise per unit
  const displayPrice =
    addOn.pricePerSquareMeter && squareMeters ? squareMeters * unitPrice : unitPrice;

  const priceLabel = addOn.pricePerSquareMeter
    ? `${formatPrice(unitPrice)}/m²`
    : formatPrice(unitPrice);

  const handleCardClick = (e: React.MouseEvent) => {
    if (disabled) {
      return;
    }
    // Only toggle if clicking on the card body, not on price or counter buttons
    const target = e.target as HTMLElement;
    // Check if click is on price element or its children
    if (target.closest('[data-price-element]')) {
      return; // Don't toggle if clicking price
    }
    // Check if click is on counter buttons (but not the card button itself)
    const clickedButton = target.closest('button[type="button"]');
    if (clickedButton && clickedButton !== e.currentTarget) {
      return; // Don't toggle if clicking counter buttons
    }
    onToggle();
    if (onChange) {
      const newQuantity = isSelected ? 0 : 1;
      onChange(addOn.id, newQuantity);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
      if (onChange) {
        const newQuantity = isSelected ? 0 : 1;
        onChange(addOn.id, newQuantity);
      }
    }
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || isAtMax) {
      return;
    }
    onIncrement();
    if (onChange) {
      onChange(addOn.id, quantity + 1);
    }
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) {
      return;
    }
    onDecrement();
    if (onChange) {
      const newQuantity = quantity <= 1 ? 0 : quantity - 1;
      onChange(addOn.id, newQuantity);
    }
  };

  const handleCounterKeyDown = (e: React.KeyboardEvent, action: 'increment' | 'decrement') => {
    e.stopPropagation();
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (action === 'increment') {
        if (isAtMax) {
          return;
        }
        onIncrement();
        if (onChange) {
          onChange(addOn.id, quantity + 1);
        }
      } else {
        onDecrement();
        if (onChange) {
          const newQuantity = quantity <= 1 ? 0 : quantity - 1;
          onChange(addOn.id, newQuantity);
        }
      }
    }
  };

  return (
    <div
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role='button'
      tabIndex={disabled ? -1 : 0}
      className={cn(
        'group flex flex-col items-center gap-2 bg-transparent px-2 pb-2 pt-4 text-center transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        isSelected && '-translate-y-1',
        className,
      )}
      aria-pressed={isSelected}
      aria-label={`${addOnName} - ${formatPrice(unitPrice)}`}
    >
      <Image
        src={addOn.svgPath}
        alt={addOnName}
        width={64}
        height={64}
        className={cn(
          'h-16 w-16 shrink-0 object-contain drop-shadow-md transition-all duration-200',
          !isSelected &&
            !disabled &&
            'opacity-60 grayscale group-hover:opacity-100 group-hover:grayscale-0',
        )}
        unoptimized
      />
      <p
        className={cn(
          'text-base font-bold leading-snug',
          isSelected ? 'text-orange-peel-600' : 'text-oxford-blue',
        )}
      >
        {addOnName}
      </p>
      {!isSelected ? (
        <div
          data-price-element
          className='text-xs font-semibold tabular-nums text-oxford-blue/50 pointer-events-none'
        >
          {priceLabel}
        </div>
      ) : (
        <>
          <div className='flex items-center justify-center gap-2'>
            <button
              type='button'
              onClick={handleDecrement}
              onKeyDown={e => handleCounterKeyDown(e, 'decrement')}
              disabled={quantity === 0 || disabled}
              className='flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition-colors hover:border-gray-400 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-1'
              aria-label={decreaseLabel}
            >
              <Minus className='h-3.5 w-3.5' />
            </button>
            <span
              className='min-w-[1.75rem] text-center text-xl font-extrabold tabular-nums text-oxford-blue'
              aria-label={`Quantity: ${quantity}`}
            >
              {quantity}
            </span>
            <button
              type='button'
              onClick={handleIncrement}
              onKeyDown={e => handleCounterKeyDown(e, 'increment')}
              disabled={disabled || isAtMax}
              className='flex h-8 w-8 items-center justify-center rounded-full bg-orange-peel-500 text-white transition-colors hover:bg-orange-peel-600 disabled:cursor-not-allowed disabled:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-1'
              aria-label={increaseLabel}
            >
              <Plus className='h-3.5 w-3.5' />
            </button>
          </div>
          <div
            data-price-element
            className='text-xs font-bold tabular-nums text-orange-peel-600 pointer-events-none'
          >
            {isSelected && addOn.pricePerSquareMeter && squareMeters
              ? formatPrice(displayPrice)
              : priceLabel}
          </div>
        </>
      )}
      <span
        aria-hidden
        className={cn(
          'mt-1 h-1 w-10 rounded-full transition-colors',
          isSelected ? 'bg-orange-peel-500' : 'bg-transparent',
        )}
      />
    </div>
  );
};

type AddOnCardProps = Omit<AddOnCardViewProps, 'labels'>;

/**
 * Add-on card for the booking flow. Resolves the title and the counter aria
 * labels from the booking add-on catalog, and the price from the admin
 * pricing config so the card matches what the customer is charged.
 */
export const AddOnCard: React.FC<AddOnCardProps> = props => {
  const getAddOnName = useAddOnDisplayName();
  const tAddOns = useTranslations('booking.wizard.addOns');
  const publicPricing = usePublicPricing();
  const name = getAddOnName(props.addOn.id);
  const configuredPrice = publicPricing.addOnsPricing?.[props.addOn.id];

  return (
    <AddOnCardView
      {...props}
      price={props.price ?? configuredPrice ?? props.addOn.price}
      labels={{
        name,
        decreaseQuantity: tAddOns('decreaseQuantity', { name }),
        increaseQuantity: tAddOns('increaseQuantity', { name }),
      }}
    />
  );
};
